import { isNil } from 'lodash';
import { isEmpty, keys, uniq } from 'ramda';
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import Button from '~/components/Button';
import { BreadcrumbType } from '~/components/ContentContainer/components/Breadcrumbs';
import ContentContainerDefault from '~/components/ContentContainer/Default';
import EditableText from '~/components/EditableText';
import TipBanner from '~/components/TipBanner';
import ChatLink from '~/components/TipBanner/ChatLink';
import JustificationContainer from '~/components/JustificationContainer';
import Link from '~/components/Link';
import Dialog from '~/components/ModalsV2/Dialog';
import Overlay from '~/components/ModalsV2/Overlay';
import NewSaveBar, { ChangeError } from '~/components/NewSaveBar';
import { Body, Heading4, Variant } from '~/components/Typography/index';
import { reportErrorToTracker } from '~/util/assertion';
import { findDifference } from '~/util/object';
import FieldsContainer, { ZapierFields } from './components/FieldsContainer';
import TEST_ID from './index.testid';
import getDuplicateKeys from './utils/getDuplicateKeys';
import { useLocation } from '@reach/router';

export type Props = {
  dataTestId?: string;

  /** The breadcrumbs to display */
  breadcrumbs: Array<BreadcrumbType>;

  /** Event or trigger name */
  headerName: string;

  /** Body description or other nested elements */
  description: React.ReactNode;

  /** Initial fields that come from query */
  initialFields: ZapierFields;

  /** Link to go back from this page */
  goBackLink: string;

  /** What to do on Save Bar save button */
  onSave: (name: string, fields: ZapierFields) => Promise<void>;

  /** We're saving, disable buttons and whatnot */
  loading?: boolean;

  /** What to do on delete button */
  onDelete?: () => void;
};

export const text = {
  tip: (
    <>
      Lees hoe je de koppeling moet instellen in
      <Link to="#"> onze kennisbank </Link>. Kom je er niet uit? <br />
      <br />
      <ChatLink linkText="Start een chat met ons." />
    </>
  ),
  deleteHeading: 'Koppeling verwijderen',
  duplicateKeyErrorMessage:
    'Dezelfde key mag niet meerdere keren gebruikt worden',
  modal: {
    delete: {
      title: (name: string) => `Verwijder uitgaande koppeling “${name}”?`,
      body: 'Weet je zeker dat je deze koppeling wilt verwijderen? Dit kan niet ongedaan worden.',
      confirm: 'Verwijderen',
      cancel: 'Afbreken',
    },
    cancel: {
      title: (_name: string) => `Weet je zeker dat je wilt afbreken?`,
      body: 'Weet je zeker dat je al je wijzigingen wilt annuleren? Dit kan niet ongedaan worden.',
      confirm: 'Bevestig',
      cancel: 'Afbreken',
    },
  },
  emptyInputError: 'Het veld kan niet leeg zijn',
  keyInputError: 'Alleen kleine letters a-z en cijfers 0-9 toegestaan',
};

export const KEY_REGEX = /^[a-z0-9_]+$/;
export const isValidKey = (value: string) => {
  if (value.length === 0) return text.emptyInputError;
  return KEY_REGEX.test(value) ? true : text.keyInputError;
};

const runValidation = (validationFunctions: Array<Function>, value: unknown) =>
  validationFunctions.reduce<Array<string>>((errors, fn) => {
    const result = fn(value);
    if (result === true) return errors;
    errors.push(result);
    return uniq(errors);
  }, []);

export type ErrorObj = {
  key: Array<string> | null;
  label: Array<string> | null;
};

const Details: React.FC<Props> = ({
  dataTestId,
  breadcrumbs,
  headerName,
  description,
  initialFields,
  children,
  goBackLink,
  onSave,
  onDelete,
  loading = false,
}) => {
  const location = useLocation();
  const isNewConnection =
    location.pathname === '/-/apps/zapier/triggers/new' ||
    location.pathname === '/-/apps/zapier/events/new';

  const [updatedFields, setUpdatedFields] =
    useState<ZapierFields>(initialFields);
  const [changes, setChanges] = useState<number>(0);
  const [showModal, setShowModal] = useState<'delete' | 'cancel' | null>(null);
  const [duplicateKeys, setDuplicateKeys] = useState<Array<string>>(
    getDuplicateKeys(updatedFields),
  );

  useEffect(() => {
    setUpdatedFields(initialFields);
  }, [initialFields]);

  const validations = {
    label: [(value: any) => (!isEmpty(value) ? true : text.emptyInputError)],
    key: [
      (value: any) => isValidKey(value),
      (value: any) =>
        duplicateKeys.includes(value) ? text.duplicateKeyErrorMessage : true,
    ],
  };

  const getChanges = (fields: ZapierFields): number => {
    let differences = 0;

    /**
     * Any fields removed / mutated
     */
    for (const field of initialFields) {
      const corresponding = fields.find(({ id }) => field.id === id);
      if (!corresponding) {
        differences++;
        continue;
      }

      const { dataIsNotEqual } = findDifference(field, corresponding);
      if (dataIsNotEqual) differences++;
    }

    /**
     * Any fields added
     */
    for (const field of fields) {
      const corresponding = initialFields.find(({ id }) => field.id === id);
      if (!corresponding) differences++;
    }

    return differences;
  };

  const errors = updatedFields.map(field =>
    keys(field).reduce((acc, key) => {
      // run validation fn for each field that we have a validation defined.
      if (!validations[key]) return acc;
      const fieldErrors = runValidation(validations[key], field[key]);
      acc[key] = fieldErrors;
      return acc;
    }, {} as ErrorObj),
  );

  const onFieldsChange = (nextFields: ZapierFields) => {
    setUpdatedFields(nextFields);
    setChanges(getChanges(nextFields));
    setDuplicateKeys(getDuplicateKeys(nextFields));
  };

  const _onSave = async () => {
    try {
      await onSave(headerName, updatedFields);
      setChanges(0);
    } catch (error) {
      reportErrorToTracker(error);
    }
  };

  const modalActions = {
    delete: () => {
      onDelete && onDelete();
      setShowModal(null);
    },
    cancel: () => {
      onFieldsChange(initialFields);
      setChanges(0);
      setShowModal(null);
    },
  };

  const changeErrors: Array<ChangeError> = errors
    .map(({ key, label }) => (key ? key[0] : null) || (label ? label[0] : null))
    .filter(err => !isNil(err))
    .map(error => ({ key: error }));

  const hasErrors = changeErrors.length !== 0;
  const saveButtonEnabled = !hasErrors && (isNewConnection || changes !== 0);

  return (
    <ContentContainerDefault breadcrumbs={breadcrumbs} data-testid={dataTestId}>
      <EditableText
        text={headerName}
        onSave={nextHeaderName => onSave(nextHeaderName, updatedFields)}
        headerSize="l"
        as="h1"
        margin={[null, null, 'm']}
        dataTestId={TEST_ID.EDITABLE_TEXT}
      />
      <NewSaveBar
        onSave={_onSave}
        onCancel={() => setShowModal('cancel')}
        changes={changes}
        errors={changeErrors}
        withGoBackLink
        goBackLink={goBackLink}
        loading={loading}
        dataTestId={TEST_ID.SAVE_BAR}
        disabled={!saveButtonEnabled}
      />
      <JustificationContainer justification="center" align="start">
        <FieldsPartContainer>
          <Body margin={[null, null, 'l', null]}>{description}</Body>
          <FieldsContainer
            fields={updatedFields}
            errors={errors}
            duplicateKeys={duplicateKeys}
            onFieldsChange={onFieldsChange}
          />
          {children}
          {onDelete && (
            <DeletePartContainer>
              <Heading4 variant={Variant.primary}>
                {text.deleteHeading}
              </Heading4>
              <Button
                label="Verwijder koppeling"
                appearance="danger"
                ghost
                size="medium"
                onClick={() => setShowModal('delete')}
                dataTestId={TEST_ID.DELETE}
              />
            </DeletePartContainer>
          )}
        </FieldsPartContainer>

        <TipBanner
          id="zapier-details-tip"
          headerText="Hulp nodig?"
          margin={['l', null, null, 'l']}
        >
          {text.tip}
        </TipBanner>
      </JustificationContainer>
      {showModal !== null && (
        <Overlay onClose={() => setShowModal(null)}>
          <Dialog
            dataTestId={TEST_ID.MODAL}
            header={text.modal[showModal].title(headerName)}
            body={text.modal[showModal].body}
            onConfirm={modalActions[showModal]}
            cancelAction={{
              label: text.modal[showModal].cancel,
            }}
            confirmAction={{
              icon: 'trashcan',
              appearance: 'danger',
              label: text.modal[showModal].confirm,
            }}
          />
        </Overlay>
      )}
    </ContentContainerDefault>
  );
};

const FieldsPartContainer = styled.div<{}>(
  ({ theme }) => css`
    background: ${theme.color('white')};
    padding: ${theme.space('xl')};
    margin-top: ${theme.space('l')};
    width: 100%;
  `,
);

const DeletePartContainer = styled.div<{}>(
  ({ theme }) => css`
    margin-top: ${theme.space('m')};
  `,
);

export default Details;
